import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import { applyAIProfile, postAIGeminiAutofill } from "../../api/profile";
import type { OnboardingProfileData } from "../../features/onboarding/types";

export type ImportedFileInfo = { name: string; size: number };
const MAX_AUTOFILL_FILE_SIZE_BYTES = 1 * 1024 * 1024; // 1 MB

type PdfImportDropzoneProps = {
  onDataExtracted: (data: Partial<OnboardingProfileData>) => void;
  extraDetails?: Record<string, any>;
  disabled?: boolean;
  /**
   * Controlled state for the "successfully uploaded" indicator. When provided,
   * the indicator's persistence is owned by the parent (so it survives
   * navigation between onboarding steps). When omitted, the component falls
   * back to its own local state.
   */
  uploadedFile?: ImportedFileInfo | null;
  onUploadedFileChange?: (file: ImportedFileInfo | null) => void;
};

const asString = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  return "";
};

const asStringList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => asString(item)).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const pickFirstString = (source: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = asString(source[key]);
    if (value) return value;
  }
  return "";
};

const pickFirstList = (source: Record<string, unknown>, keys: string[]): string[] => {
  for (const key of keys) {
    const value = asStringList(source[key]);
    if (value.length) return value;
  }
  return [];
};

const asObject = (value: unknown): Record<string, unknown> => {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
};

const extractOperatingYearsRaw = (
  basicInfo: Record<string, unknown>,
  objective: Record<string, unknown>,
  fundingExtraDetails: Record<string, unknown>
) => {
  const objectiveOrgProfile = asObject(objective.organisation_profile);
  const direct =
    pickFirstString(basicInfo, ["operating_years", "years_operating"]) ||
    pickFirstString(fundingExtraDetails, ["years_operating"]) ||
    pickFirstString(objectiveOrgProfile, ["years_operating", "operating_years"]);
  if (direct) return direct;

  const foundedYearRaw = asString(basicInfo.founded_year);
  const foundedYear = Number(foundedYearRaw);
  const currentYear = new Date().getFullYear();
  if (!Number.isNaN(foundedYear) && foundedYear > 1900 && foundedYear <= currentYear) {
    return String(currentYear - foundedYear);
  }

  return "";
};

const toOnboardingPatch = (
  payload?: Record<string, unknown>
): Partial<OnboardingProfileData> => {
  if (!payload || typeof payload !== "object") return {};

  const basicInfo =
    payload.basic_info && typeof payload.basic_info === "object"
      ? (payload.basic_info as Record<string, unknown>)
      : {};
  const objective =
    payload.objective && typeof payload.objective === "object"
      ? (payload.objective as Record<string, unknown>)
      : {};
  const project =
    payload.project && typeof payload.project === "object"
      ? (payload.project as Record<string, unknown>)
      : {};
  const impact =
    payload.impact && typeof payload.impact === "object"
      ? (payload.impact as Record<string, unknown>)
      : {};
  const funding =
    payload.funding && typeof payload.funding === "object"
      ? (payload.funding as Record<string, unknown>)
      : {};
  const fundingRange = asObject(funding.funding_range);
  const fundingExtraDetails = asObject(funding.funding_extra_details);

  const patch: Partial<OnboardingProfileData> = {};

  patch.organizationName = pickFirstString(basicInfo, [
    "organization_name",
    "organisation_name",
    "company_name",
    "name",
  ]);
  patch.organizationType = pickFirstString(basicInfo, [
    "organization_type",
    "organisation_type",
    "entity_type",
  ]);
  patch.country = pickFirstString(basicInfo, ["country", "region"]);
  patch.postcode = pickFirstString(basicInfo, ["postcode", "postal_code", "zip_code"]);
  patch.operatingScope =
    pickFirstString(basicInfo, ["operating_scope", "operating_area", "scope"]) ||
    pickFirstString(fundingExtraDetails, [
      "onboarding_operating_scope",
      "operating_scope",
      "operating_area",
    ]);
  patch.websiteUrl = pickFirstString(basicInfo, ["website", "website_url", "url"]);

  patch.whatWeDo =
    pickFirstString(project, ["what_we_do", "project_description", "description"]) ||
    pickFirstString(objective, ["mission", "objective"]) ||
    pickFirstString(impact, ["summary"]);

  const primaryBeneficiaries = pickFirstList(impact, [
    "primary_beneficiaries",
    "beneficiaries",
  ]);
  patch.primaryBeneficiaries = primaryBeneficiaries.length
    ? primaryBeneficiaries
    : pickFirstList(objective, ["beneficiary_groups"]);

  // Gather focus-theme candidates from every source the AI might use,
  // not just the first non-empty one. Some payloads put a coarse list in
  // `focus_themes` (e.g. ["bio diversity"]) while burying additional
  // themes inside the `sector` string ("Arts, Culture & Creative
  // Industries, bio diversity"). The parent's matchMulti then dedupes
  // and resolves each candidate against the dropdown options.
  const focusThemeCandidates: string[] = [];
  focusThemeCandidates.push(...pickFirstList(objective, ["focus_themes", "area_focus"]));
  focusThemeCandidates.push(...pickFirstList(project, ["focus_themes"]));
  const basicSector = asString(basicInfo.sector);
  if (basicSector) focusThemeCandidates.push(basicSector);
  const objectiveSector = asString(objective.sector);
  if (objectiveSector) focusThemeCandidates.push(objectiveSector);
  patch.focusThemes = focusThemeCandidates;

  patch.operatingYears = extractOperatingYearsRaw(basicInfo, objective, fundingExtraDetails);
  patch.annualTurnoverBand =
    pickFirstString(funding, ["annual_turnover_band", "turnover_band"]) ||
    pickFirstString(fundingExtraDetails, ["annual_turnover_band", "turnover_band"]) ||
    pickFirstString(basicInfo, ["size"]);
  patch.fundingTypes = pickFirstList(funding, ["funding_types", "types"]);
  patch.fundingAmountBand =
    pickFirstString(funding, ["funding_amount_band", "amount_band", "funding_range"]) ||
    pickFirstString(fundingRange, ["range", "label"]) ||
    pickFirstString(fundingExtraDetails, ["onboarding_funding_amount_band"]);
  patch.fundingTimeline =
    pickFirstString(funding, ["funding_timeline", "timeline", "urgency"]) ||
    pickFirstString(fundingExtraDetails, ["funding_timeline"]);

  return Object.fromEntries(
    Object.entries(patch).filter(([, value]) => {
      if (typeof value === "string") return Boolean(value.trim());
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null;
    })
  ) as Partial<OnboardingProfileData>;
};

const getErrorMessage = (error: any): string => {
  const fromApi =
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message;
  return asString(fromApi) || "Import failed. Please try again.";
};

export default function PdfImportDropzone({
  onDataExtracted,
  extraDetails,
  disabled = false,
  uploadedFile: uploadedFileProp,
  onUploadedFileChange,
}: PdfImportDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadPhase, setUploadPhase] = useState<"idle" | "uploading" | "processing" | "done">(
    "idle"
  );
  const [internalUploadedFile, setInternalUploadedFile] = useState<ImportedFileInfo | null>(
    null
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Controlled vs. uncontrolled: when the parent passes `uploadedFile`,
  // they own persistence (survives step navigation). Otherwise we keep
  // it locally as before.
  const isControlled = uploadedFileProp !== undefined;
  const uploadedFile = isControlled ? uploadedFileProp : internalUploadedFile;
  const updateUploadedFile = (file: ImportedFileInfo | null) => {
    if (isControlled) {
      onUploadedFileChange?.(file);
    } else {
      setInternalUploadedFile(file);
      onUploadedFileChange?.(file);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const processDocument = async (file: File) => {
    setProcessing(true);
    setUploadPhase("uploading");
    setUploadProgress(0);
    updateUploadedFile({ name: file.name, size: file.size });

    try {
      const autofill = await postAIGeminiAutofill(file, extraDetails, (percent) => {
        setUploadProgress(percent);
        if (percent >= 100) {
          setUploadPhase("processing");
        }
      });

      // Ensure progress bar shows 100% before moving on
      setUploadProgress(100);
      setUploadPhase("processing");

      const extractedPatch = toOnboardingPatch(autofill.data);

      if (Object.keys(extractedPatch).length > 0) {
        onDataExtracted(extractedPatch);
      }

      if (typeof autofill.ai_profile_id === "number") {
        await applyAIProfile(autofill.ai_profile_id);
      }

      setUploadPhase("done");
      toast.success("Document imported and AI profile applied.");
    } catch (error) {
      setUploadPhase("idle");
      updateUploadedFile(null);
      setUploadProgress(0);
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  const handleFiles = (files: FileList | File[]) => {
    const file = Array.from(files)[0];
    if (file) {
      if (file.size > MAX_AUTOFILL_FILE_SIZE_BYTES) {
        toast.error("File is too large. Maximum allowed size is 1 MB.");
        return;
      }
      processDocument(file);
    }
  };

  const resetUpload = () => {
    setUploadPhase("idle");
    updateUploadedFile(null);
    setUploadProgress(0);
  };

  const isActivelyUploading =
    uploadPhase === "uploading" || uploadPhase === "processing";
  // Show the success indicator if we have a remembered file and we are not
  // currently mid-upload. This covers both the live "done" case and the
  // post-remount case (where uploadPhase has reset to "idle" but the file
  // info is still held by the parent).
  const showDoneState = !isActivelyUploading && Boolean(uploadedFile);

  return (
    <div className="mb-6 rounded-[32px] border border-[#E5E7EB] bg-[#F8FAFC] p-5">
      <div className="text-sm font-semibold text-[#0F172A] mb-3">Quick AI Import</div>
      <p className="text-sm text-[#475569] mb-4">
        Upload a PDF, DOC, or similar file and we will auto-fill your onboarding profile
        using AI.
      </p>

      <button
        type="button"
        disabled={disabled || processing}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled && !processing) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!disabled && !processing && event.dataTransfer.files.length > 0) {
            handleFiles(event.dataTransfer.files);
          }
        }}
        className={`flex min-h-[100px] w-full flex-col items-center justify-center rounded-3xl border border-dashed px-5 py-4 text-center transition ${
          dragging
            ? "border-[#93C893] bg-[#EEF9EE]"
            : disabled || processing
            ? "border-[#CBD5E1] bg-[#F1F5F9] cursor-not-allowed"
            : "border-[#CBD5E1] bg-white hover:border-[#93C893] hover:bg-[#EEF9EE]"
        }`}
      >
        <span className="material-icons mb-2 rounded-full bg-white p-2 text-[20px] text-[#166534] shadow-sm">
          {processing ? "hourglass_empty" : "upload"}
        </span>
        <span className="text-sm font-semibold text-[#0F172A]">
          {processing ? "Processing document..." : "Drop document here or click to upload"}
        </span>
        <span className="mt-1 text-xs text-[#6B7280]">
          PDF, DOC, DOCX, PPT, PPTX (max 1 MB)
        </span>
      </button>

      {/* Upload progress / success state. The success state persists across
          navigation because `uploadedFile` is held by the parent. */}
      {uploadedFile && (isActivelyUploading || showDoneState) && (
        <div
          className={`mt-3 flex items-center gap-3 rounded-2xl border px-4 py-3 ${
            showDoneState
              ? "border-[#BBF7D0] bg-[#F0FDF4]"
              : "border-[#E5E7EB] bg-white"
          }`}
        >
          <span
            className={`material-icons rounded-full p-2 text-[18px] ${
              showDoneState
                ? "bg-[#DCFCE7] text-[#166534]"
                : "bg-[#EEF9EE] text-[#166534]"
            }`}
          >
            {showDoneState ? "check_circle" : "description"}
          </span>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-sm font-semibold text-[#0F172A]">
                {uploadedFile.name}
              </span>
              <span className="shrink-0 text-xs text-[#6B7280]">
                {formatFileSize(uploadedFile.size)}
              </span>
            </div>

            {isActivelyUploading ? (
              <>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
                  <div
                    className="h-full rounded-full bg-[#166534] transition-all duration-200 ease-out"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-[#6B7280]">
                  <span>
                    {uploadPhase === "processing"
                      ? "Processing document..."
                      : "Uploading..."}
                  </span>
                  <span className="font-medium text-[#0F172A]">
                    {uploadProgress}%
                  </span>
                </div>
              </>
            ) : (
              <p className="mt-0.5 text-xs text-[#166534]">
                File "{uploadedFile.name}" successfully uploaded
              </p>
            )}
          </div>

          {showDoneState && (
            <button
              type="button"
              onClick={resetUpload}
              className="ml-1 rounded-full p-1 text-[#6B7280] transition hover:bg-white hover:text-[#0F172A]"
              aria-label="Remove uploaded file"
            >
              <span className="material-icons text-[18px]">close</span>
            </button>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
        className="hidden"
        onChange={(event) => {
          if (event.target.files?.length) {
            handleFiles(event.target.files);
          }
          event.target.value = "";
        }}
      />
    </div>
  );
}
